import React, { useContext, useEffect, useState, useRef, useMemo } from 'react'
import LoadMoreButton from '../../../../components/LoadMoreButton'
import axios from '../../../../config/axios'
import pen from '../../../../assets/productions/pen.svg'
import { DaysOfTheWeekDiv, DayOfTheWeek } from '../../../../components/AddCompanyModal/DisponibilityComponent/style'
import { GlobalTable, HeaderLine, Td, TdButtons, Th, Tr } from '../../../../components/styles/table-style'
import { ConfigMenuWrapper, ConfigMenuButton } from '../../SteelOrders/SteelOrdersTable/style'
import { useReactTable, getCoreRowModel, getSortedRowModel, flexRender, ColumnDef, SortingState } from '@tanstack/react-table'
import { TableContainer } from '../style'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Filter } from '../../../../components/SubHeaderFilter/types'
import FieldsManager, { FieldsManagerType } from '../../../../components/FieldManager'
import { Settings, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useTooltip } from '../../../../components/Tooltip/useTooltip'

const EmailContainer = styled.div`
  max-width: 150px;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;


interface CompanyInterface {
  id: number
  companyTypes: string[]
  cnpj: string
  companyName: string
  stateRegistration: string
  address: string
  phone: string
  email: string
  contactName: string
}

interface TableCompanyType {
  whichFieldsToShow: FieldsManagerType
  setFields: React.Dispatch<React.SetStateAction<FieldsManagerType>>
  dropdownOpen: React.Dispatch<React.SetStateAction<boolean>>
  isDropdownOpen: boolean
  appliedFilters?: Filter[]
  refresh?: boolean
  personType?: 'Jurídica' | 'Física' | 'Própria' | 'Transportadora' | null
  search?: string
}

const TableCompanies: React.FC<TableCompanyType> = ({ whichFieldsToShow, setFields, dropdownOpen, isDropdownOpen, appliedFilters, personType = null, search }) => {
  const [companies, setCompanies] = useState<CompanyInterface[]>([])
  const [totalCompanies, setTotalCompanies] = useState<number | undefined>(undefined)
  const [lastFetchedCount, setLastFetchedCount] = useState<number>(0)
  const [quantity, setQuantity] = useState<number>(50)
  const [sorting, setSorting] = useState<SortingState>([])
  const { tooltipElement, handleMouseEnter, handleMouseLeave, handleCellClick } = useTooltip({
    maxWidth: 500,
    maxHeight: 280,
    rowHeight: 22,
    padding: 16,
    verticalGap: 4,
  })
  const emailRefs = useRef<{ [key: string]: HTMLDivElement | null }>({})
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const columnMenuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (columnMenuRef.current && !columnMenuRef.current.contains(event.target as Node)) {
        dropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [dropdownOpen])

  const computeTypeLabel = (company: any) => {
    const types = company.companyTypes || []
    if (Array.isArray(types)) {
      const typeNames = types.map((first: any) => {
        if (typeof first === 'string') return first
        if (typeof first === 'number') return String(first)
        return first?.typeName || first?.companyType?.typeName || ''
      }).filter(Boolean)
      return typeNames.join(', ')
    }
    try {
      return String(types)
    } catch {
      return ''
    }
  }

  const handleEdit = (id: number) => {
    navigate('edit/?id=' + id)
  }

  const handleEmailMouseEnter = (e: React.MouseEvent, email: string, companyId: number) => {
    const emailRef = emailRefs.current[`email-${companyId}`]
    if (emailRef && email) {
      const emailArray = email.split(',').map(e => e.trim()).filter(e => e.length > 0)
      const isOverflowing = emailRef.scrollWidth > emailRef.clientWidth

      if (isOverflowing || emailArray.length > 1) {
        const tooltipText = emailArray.join('\n')
        handleMouseEnter(e, tooltipText)
      }
    }
  }

  const handleEmailClick = (e: React.MouseEvent, email: string, companyId: number) => {
    const emailRef = emailRefs.current[`email-${companyId}`]
    if (emailRef && email) {
      const emailArray = email.split(',').map(e => e.trim()).filter(e => e.length > 0)
      const isOverflowing = emailRef.scrollWidth > emailRef.clientWidth

      if (isOverflowing || emailArray.length > 1) {
        const tooltipText = emailArray.join('\n')
        handleCellClick(e, tooltipText)
      }
    }
  }

  const handleAddressMouseEnter = (e: React.MouseEvent, address: string, companyId: number) => {
    const addressRef = emailRefs.current[`address-${companyId}`]
    if (addressRef && address) {
      const isOverflowing = addressRef.scrollWidth > addressRef.clientWidth
      if (isOverflowing) {
        handleMouseEnter(e, address)
      }
    }
  }

  const handleAddressClick = (e: React.MouseEvent, address: string, companyId: number) => {
    const addressRef = emailRefs.current[`address-${companyId}`]
    if (addressRef && address) {
      const isOverflowing = addressRef.scrollWidth > addressRef.clientWidth
      if (isOverflowing) {
        handleCellClick(e, address)
      }
    }
  }

  const handleEmailMouseLeave = () => {
    handleMouseLeave()
  }

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['companies', appliedFilters, quantity, search, personType],
    queryFn: async () => {
      console.log('🛰️ [DEBUG FRONT] queryFn iniciado. params:', { search, personType, appliedFilters, quantity });
      const params: any = {
        limit: quantity,
        offset: 0,
      }
      if (personType === 'Própria') {
        params.isMain = 'true'
      } else {
        params.excludeMainCompany = 'true'
      }

      if (personType === 'Transportadora') {
        params.companyType = 'Transportadora'
      }

      if (search && search.trim()) {
        params.query = search.trim()
      }
      if (personType === 'Jurídica') {
        params.personType = 'juridica'
      } else if (personType === 'Física') {
        params.personType = 'fisica'
      }
      if (appliedFilters && Array.isArray(appliedFilters) && appliedFilters.length > 0) {
        params.filters = JSON.stringify(appliedFilters)
      }

      console.log('🛰️ [DEBUG FRONT] enviando GET /companies com params:', JSON.stringify(params));

      const resp = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/companies`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })

      console.log('🛰️ [DEBUG FRONT] resposta recebida do GET:', resp?.status, 'tamanho:', Array.isArray(resp?.data) ? resp.data.length : typeof resp?.data, 'payload:', resp?.data);

      let countValue: number | undefined = undefined

      const countResp = await axios.get(`${process.env.REACT_APP_GATEWAY_BASE_URL}/companies/count`, {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })

      try {
        const headerTotal = countResp?.headers?.['x-total-count'] ?? countResp?.headers?.['X-Total-Count']
        const candidateValues = [countResp?.data, countResp?.data?.totalItems, countResp?.data?.total, headerTotal]
        for (const v of candidateValues) {
          const n = Number(v)
          if (Number.isFinite(n)) { countValue = n; break }
        }
      } catch (err) {
        countValue = undefined
      }

      try {
        const listLength = Array.isArray(resp?.data) ? resp.data.length : undefined
        if (typeof countValue === 'number' && typeof listLength === 'number' && listLength > countValue) {
          setTotalCompanies(listLength)
        } else {
          setTotalCompanies(countValue)
        }
      } catch (err) {
        setTotalCompanies(undefined)
      }

      return resp.data
    }
  })

  useEffect(() => {
    if (data) {
      const original = Array.isArray(data) ? data : []
      setLastFetchedCount(original.length)
      setCompanies(original)
    } else if (isError) {
      setCompanies([])
    }
  }, [data, isError])

  useEffect(() => {
    setQuantity(50)
  }, [personType, appliedFilters, search])

  const visibleFields = whichFieldsToShow.whichFieldsToShow.filter(field => field.checked && !['cnpj', 'cpf', 'companyName', 'contactName', 'stateRegistration'].includes(field.field))

  const columns = useMemo<ColumnDef<any>[]>(() => {
    const cols: ColumnDef<any>[] = []

    const isChecked = (fieldKey: string) => {
      const f = whichFieldsToShow.whichFieldsToShow.find((x: any) => x.field === fieldKey)
      return f ? f.checked : true
    }

    if (personType === null || personType === 'Própria' || personType === 'Transportadora') {
      cols.push({
        id: 'doc',
        header: 'CNPJ/CPF',
        cell: (info: any) => {
          const c = info.row.original
          const cnpj = (c.cnpj || '').toString().trim()
          const cpf = (c.cpf || '').toString().trim()
          return cnpj || cpf || ''
        },
      })
      cols.push({
        id: 'name',
        header: 'Razão Social / Nome',
        cell: (info: any) => {
          const c = info.row.original
          const companyName = (c.companyName || '').toString().trim()
          const contactName = (c.contactName || '').toString().trim()
          const isMainCompany = c.isMain
          const labelMatrizFilial = isMainCompany ? (c.tipoEstabelecimento || (c.cnpj ? (c.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : 'Matriz')) : ''

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
              {labelMatrizFilial && (
                <span style={{
                  backgroundColor: '#e0f2fe',
                  color: '#0369a1',
                  fontSize: '11px',
                  fontWeight: '600',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  flexShrink: 0
                }}>
                  {labelMatrizFilial}
                </span>
              )}
              <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{companyName || contactName || ''}</span>
            </div>
          )
        },
      })
    } else if (personType === 'Jurídica') {
      cols.push({
        id: 'cnpj',
        header: 'CNPJ',
        cell: (info: any) => (info.row.original.cnpj || '').toString().trim(),
      })
      cols.push({
        id: 'companyName',
        header: 'Razão Social',
        cell: (info: any) => {
          const c = info.row.original
          const isMainCompany = c.isMain
          const labelMatrizFilial = isMainCompany ? (c.tipoEstabelecimento || (c.cnpj ? (c.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : 'Matriz')) : ''

          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', overflow: 'hidden' }}>
              {labelMatrizFilial && (
                <span style={{
                  backgroundColor: '#e0f2fe',
                  color: '#0369a1',
                  fontSize: '11px',
                  fontWeight: '600',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  textTransform: 'uppercase',
                  flexShrink: 0
                }}>
                  {labelMatrizFilial}
                </span>
              )}
              <span style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>{(c.companyName || '').toString().trim()}</span>
            </div>
          )
        },
      })
      cols.push({
        id: 'stateRegistration',
        header: 'Inscrição Estadual',
        cell: (info: any) => (info.row.original.stateRegistration || '').toString().trim(),
      })
    } else if (personType === 'Física') {
      cols.push({
        id: 'cpf',
        header: 'CPF',
        cell: (info: any) => (info.row.original.cpf || '').toString().trim(),
      })
      cols.push({
        id: 'companyName',
        header: 'Nome',
        cell: (info: any) => {
          const c = info.row.original
          const contactName = (c.contactName || '').toString().trim()
          const companyName = (c.companyName || '').toString().trim()
          return contactName || companyName || ''
        },
      })
    }

    const dynamicFields = visibleFields.map((field) => {
      const key = field.field
      const formatAddress = (company: any) => {
        const safe = (v: any) => {
          if (!v && v !== 0) return ''
          if (typeof v === 'string') return v
          if (typeof v === 'number') return String(v)
          if (typeof v === 'object') return (v && (v.name || v.label || v.typeName)) ? (v.name || v.label || v.typeName) : ''
          return String(v)
        }

        const street = safe(company.street)
        const number = safe(company.number)
        const neighborhood = safe(company.neighborhood)
        const city = safe(company.city)
        const state = safe(company.state)
        const rawCep = company.cep || ''
        const cepDigits = String(rawCep).replace(/\D/g, '')
        const cepPadded = cepDigits ? cepDigits.padStart(8, '0') : ''
        const cepFormatted = cepPadded ? `${cepPadded.slice(0, 5)}-${cepPadded.slice(5)}` : ''
        const parts: string[] = []
        if (street) parts.push(`${street}${number ? `, ${number}` : ''}`)
        if (neighborhood) parts.push(neighborhood)
        if (city) parts.push(city)
        if (state) parts.push(state)
        const main = parts.join(' - ')
        const result = cepFormatted ? `${main}${main ? ' - ' : ''}${cepFormatted}` : main
        return result.trim() ? result : ''
      }
      if (key === 'companyTypes') {
        return {
          accessorKey: key,
          header: field.fieldName,
          cell: (info: any) => computeTypeLabel(info.row.original),
        }
      }
      if (key === 'email') {
        return {
          accessorKey: key,
          header: field.fieldName,
          cell: (info: any) => (
            <EmailContainer
              ref={(el) => emailRefs.current[`email-${info.row.original.id}`] = el}
              onMouseEnter={(e: any) => handleEmailMouseEnter(e, info.getValue() ?? '', info.row.original.id)}
              onMouseLeave={handleEmailMouseLeave}
              onClick={(e: any) => handleEmailClick(e, info.getValue() ?? '', info.row.original.id)}
            >
              {info.getValue() || 'Nenhum e-mail'}
            </EmailContainer>
          ),
        }
      }
      if (key === 'address') {
        return {
          accessorKey: key,
          header: field.fieldName,
          cell: (info: any) => {
            const company = info.row.original
            const addressStr = formatAddress(company) || ''
            return (
              <EmailContainer
                ref={(el) => emailRefs.current[`address-${info.row.original.id}`] = el}
                onMouseEnter={(e: any) => handleAddressMouseEnter(e, addressStr, info.row.original.id)}
                onMouseLeave={() => handleEmailMouseLeave()}
                onClick={(e: any) => handleAddressClick(e, addressStr, info.row.original.id)}
              >
                {addressStr && addressStr.trim() ? addressStr : 'Nenhum endereço cadastrado'}
              </EmailContainer>
            )
          }
        }
      }
      if (key === 'activedDays') {
        return {
          accessorKey: key,
          header: field.fieldName,
          cell: (info: any) => (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.2rem' }}>
              <DaysOfTheWeekDiv>
                {(info.row.original.activedDays || [])
                  .slice()
                  .sort((a: number, b: number) => a - b)
                  .map((dayIdx: number) => (
                    <DayOfTheWeek key={dayIdx} className={'actived'}>
                      {daysLabels[dayIdx] ?? dayIdx}
                    </DayOfTheWeek>
                  ))}
              </DaysOfTheWeekDiv>
              <div style={{ fontSize: '.8rem', color: '#666', marginTop: '2px' }}>
                {formatTime(info.row.original.startTime)} - {formatTime(info.row.original.endTime)}
              </div>
            </div>
          ),
        }
      }

      if (key === 'contactName') {
        return {
          accessorKey: key,
          header: 'Nome',
          cell: (info: any) => String(info.getValue() ?? ''),
        }
      }

      if (key === 'contribuinteIcms') {
        return {
          accessorKey: key,
          header: field.fieldName,
          cell: (info: any) => {
            const val = info.getValue()
            if (val === 'CONTRIBUINTE') return 'Contribuinte'
            if (val === 'ISENTO') return 'Isento'
            if (val === 'NAO_CONTRIBUINTE') return 'Não Contribuinte'
            return val || ''
          }
        }
      }

      if (key === 'contacts') {
        return {
          accessorKey: key,
          header: field.fieldName,
          cell: (info: any) => {
            const list = info.getValue() || []
            if (!Array.isArray(list) || list.length === 0) return 'Nenhum'
            const textToShow = list.map((c: any) => c.name || '').filter(Boolean).join(', ')

            return (
              <EmailContainer
                ref={(el) => emailRefs.current[`contacts-${info.row.original.id}`] = el}
                onMouseEnter={(e: any) => {
                  const tooltipText = list.map((c: any) => {
                    const parts = [c.name || ''];
                    if (c.phone) parts.push(c.phone);
                    if (c.email) parts.push(c.email);
                    return parts.join(' - ');
                  }).join('\n')
                  handleMouseEnter(e, tooltipText)
                }}
                onMouseLeave={() => handleEmailMouseLeave()}
                onClick={(e: any) => {
                  const tooltipText = list.map((c: any) => {
                    const parts = [c.name || ''];
                    if (c.phone) parts.push(c.phone);
                    if (c.email) parts.push(c.email);
                    return parts.join(' - ');
                  }).join('\n')
                  handleCellClick(e, tooltipText)
                }}
              >
                {textToShow}
              </EmailContainer>
            )
          }
        }
      }

      if (key === 'linkedUsers') {
        return {
          accessorKey: key,
          header: field.fieldName,
          cell: (info: any) => {
            const list = info.getValue() || []
            if (!Array.isArray(list) || list.length === 0) return 'Nenhum'
            const textToShow = list.map((lu: any) => lu.name || '').filter(Boolean).join(', ')

            return (
              <EmailContainer
                ref={(el) => emailRefs.current[`linkedUsers-${info.row.original.id}`] = el}
                onMouseEnter={(e: any) => {
                  const tooltipText = list.map((lu: any) => lu.name || '').filter(Boolean).join('\n')
                  handleMouseEnter(e, tooltipText)
                }}
                onMouseLeave={() => handleEmailMouseLeave()}
                onClick={(e: any) => {
                  const tooltipText = list.map((lu: any) => lu.name || '').filter(Boolean).join('\n')
                  handleCellClick(e, tooltipText)
                }}
              >
                {textToShow}
              </EmailContainer>
            )
          }
        }
      }

      return {
        accessorKey: key,
        header: field.fieldName,
        cell: (info: any) => String(info.getValue() ?? ''),
      }
    })

    cols.push(...dynamicFields)

    cols.push({
      id: 'actions',
      header: 'Ações',
      cell: (info: any) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', height: '100%', width: '100%', justifyContent: 'flex-start' }}>
          <button
            onClick={() => handleEdit(info.row.original.id)}
            style={{ cursor: 'pointer', border: 'none', background: 'transparent', padding: 0 }}
            aria-label='Editar empresa'
          >
            <img src={pen} alt='pen icon' style={{ cursor: 'pointer' }} />
          </button>
        </div>
      )
    })

    return cols
  }, [visibleFields, personType, whichFieldsToShow])

  const table = useReactTable({
    data: companies,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <>
      <TableContainer>
        <GlobalTable>
          <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <HeaderLine key={headerGroup.id}>
              {headerGroup.headers.map(header => (
                <Th key={header.id} onClick={header.column.getToggleSortingHandler()}>
                  {header.isPlaceholder ? null : (() => {
                    const def: any = header.column.columnDef
                    const isActions = def?.accessorKey === 'actions' || def?.id === 'actions' || String(header.id).toLowerCase().includes('actions')
                    if (isActions) {
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'inline-block' }}>{flexRender(def.header, header.getContext())}</div>
                          <ConfigMenuWrapper ref={columnMenuRef}>
                            <ConfigMenuButton onClick={(e: any) => { e.stopPropagation(); dropdownOpen((prev: any) => !prev) }} title='Mostrar/ocultar colunas' style={{ color: '#bdbdbd' }}>
                              <Settings size={20} />
                            </ConfigMenuButton>
                            {isDropdownOpen && (
                              <FieldsManager
                                dropdownOpen={dropdownOpen}
                                fields={whichFieldsToShow}
                                setFields={setFields}
                              />
                            )}
                          </ConfigMenuWrapper>
                        </div>
                      )
                    }
                    return flexRender(def.header, header.getContext())
                  })()}
                  {header.column.getCanSort() && (
                    {
                      asc: <ArrowUp size={14} />,
                      desc: <ArrowDown size={14} />,
                    }[header.column.getIsSorted() as string] ?? <ArrowUpDown size={14} style={{ opacity: 0.4 }} />
                  )}
                </Th>
              ))}
            </HeaderLine>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.length > 0 ? (
            table.getRowModel().rows.map(row => (
              <Tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <Td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</Td>
                ))}
              </Tr>
            ))
          ) : (
            <Tr>
              <Td colSpan={columns.length}>Nenhuma empresa encontrada.</Td>
            </Tr>
          )}
        </tbody>
        </GlobalTable>
        <LoadMoreButton
          currentCount={companies.length}
          totalCount={typeof totalCompanies === 'number' ? totalCompanies : undefined}
          onLoadMore={() => {
            const newQuantity = quantity + 50
            setQuantity(newQuantity)
          }}
          hasMore={typeof totalCompanies === 'number' ? companies.length < totalCompanies : lastFetchedCount >= quantity}
          isLoading={isLoading}
        />
      </TableContainer>
      {tooltipElement}
    </>
  )
}

const daysLabels = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']

const formatTime = (time: any) => {
  if (!time) return '--'
  try {
    const d = new Date(time)
    if (!isNaN(d.getTime())) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    }
    return String(time)
  } catch {
    return String(time)
  }
}

export default TableCompanies